import {
  View,
  Text,
  StyleSheet,
  TextInput,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import CustomAlert from "../../Components/CustomAlert";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import ProfileSegmentedControl from "../../Components/ProfileSegmentedControl";
import { formatNumber2Dec } from "../../utils/numberUtils";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import TextareaAutosize from "react-textarea-autosize";
import {
  hasClientProfileUnsavedChanges,
  saveClientProfileDraft,
  syncClientProfileDraftWithPersistedData,
} from "../../platform/guards/clientProfileUnsavedChanges";

const ClientProfile = observer(
  ({ client, profileDiscardResetVersion = 0 }) => {
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
  const deleteAlertTitle = "deleteClientAlertTitle-string";
  const deleteAlertText = "deleteClientAlertText-string";

  const deleteClient = async () => {
    await client.deleteClient();
  };

  useEffect(() => {
    syncClientProfileDraftWithPersistedData(client);
  }, []);

  const TextName = ({ property, firestoreProperty, placeholder }) => {
    const [keyword, setKeyword] = useState(
      client.getProperty(property) ? client.getProperty(property) : ""
    );
    const onChangeText = (text) => {
      setKeyword(text);
      client.setSettings(firestoreProperty, text);
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={[
          styles.textInputContainer,
          {
            width: screenSize === "small" ? "80%" : "45%",
            backgroundColor: Colors.whiteColor,
            borderColor: Colors.borderColor,
            borderWidth: 1,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={keyword}
          onChangeText={onChangeText}
          style={[
            styles.textInputStyle,
            Fonts.regular14,
            { color: Colors.blackColor, outline: "none", width: "100%" },
          ]}
          placeholder={t(placeholder)}
          placeholderTextColor={Colors.grayColor}
          maxLength={40}
        />
      </View>
    );
  };

  const UnmutableTextName = ({ property, placeholder, textAlign }) => {
    return (
      <View
        style={[
          styles.textInputContainer,
          {
            backgroundColor: "transparent",
            width: screenSize === "small" ? "80%" : "45%",
            paddingHorizontal: 0,
            borderWidth: 0,
          },
        ]}
      >
        <Text
          style={[
            styles.textInputStyle,
            Fonts.regular14,
            { color: Colors.grayColor, width: "100%", lineHeight: 40, textAlign: textAlign || "left" },
          ]}
          numberOfLines={1}
        >
          {client.getProperty(property)
            ? client.getProperty(property)
            : placeholder}
        </Text>
      </View>
    );
  };

  const TextNotes = () => {
    const [string, setString] = useState(client.notes ? client.notes : "");

    const handleChange = (e) => {
      const text = e.target.value;
      setString(text);
      client.setSettings("notes", text);
    };

    return (
      <View style={{ width: "100%", overflow: "hidden" }}>
        <TextareaAutosize
          value={string}
          onChange={handleChange}
          placeholder={t("privateNotePlaceholder-string")}
          style={{
            width: "100%",
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            color: Colors.blackColor,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: 14,
            fontWeight: "400",
            resize: "none",
            padding: 0,
            margin: 0,
            lineHeight: "1.4",
            textAlign: "left",
          }}
          spellCheck={false}
          maxLength={1000}
        />
      </View>
    );
  };

  const propertyMap = {
    squat: { value: client.profileSquat, settingsKey: "profile_squat" },
    benchPress: {
      value: client.profileBenchPress,
      settingsKey: "profile_bench_press",
    },
    deadlift: {
      value: client.profileDeadlift,
      settingsKey: "profile_deadlift",
    },
  };

  const CompetitionRegisterTextBox = ({ propertyCase }) => {
    const { value, settingsKey } = propertyMap[propertyCase] || {};

    const [propertyString, setPropertyString] = useState(
      value > 0 ? formatNumber2Dec(value) : ""
    );
    const [isHovered, setIsHovered] = useState(false);

    const handleChange = (text) => {
      const formattedText = text.replace(",", ".");
      const num = Number(formattedText);
      if (!isNaN(num)) {
        setPropertyString(formattedText);
        const numAux = formattedText === "" ? 0 : num + 1e-7;
        client.setSettings(settingsKey, numAux);
      }
    };

    return (
      <View
        style={[
          styles.competitionTextBoxContainer,
          {
            backgroundColor: Colors.whiteColor,
            borderColor: Colors.borderColor,
            borderWidth: 1,
            width: screenSize === "small" ? "80%" : "45%",
            minWidth: screenSize === "large" ? 100 : undefined,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={propertyString}
          onChangeText={handleChange}
          inputMode="numeric"
          style={[styles.competitionTextBoxInput, Fonts.regular14]}
          maxLength={7}
        />
      </View>
    );
  };

  const BestCompetitionLifts = () => {
    const lifts = [
      { key: "squat", label: t("squat-string") },
      { key: "benchPress", label: t("benchPress-string") },
      { key: "deadlift", label: t("deadlift-string") },
    ];

    return (
      <View
        style={[
          styles.bestLiftsContainer,
          { flexDirection: screenSize !== "large" ? "column" : "row" },
        ]}
      >
        {lifts.map((lift, index) => (
          <View
            key={lift.key}
            style={[
              styles.liftRow,
              {
                marginBottom:
                  screenSize !== "large" && index < lifts.length - 1 ? 20 : 0,
                width: screenSize !== "large" ? "100%" : undefined,
              },
            ]}
          >
            <Text
              style={[
                Fonts.regular14,
                styles.liftLabel,
                {
                  width:
                    screenSize === "small"
                      ? 120
                      : screenSize === "medium"
                      ? 180
                      : undefined,
                },
              ]}
            >
              {lift.label}
            </Text>
            <View style={styles.liftTextBoxWrapper}>
              <CompetitionRegisterTextBox propertyCase={lift.key} />
            </View>
          </View>
        ))}
      </View>
    );
  };

  const SaveButton = observer(() => {
    const saveClientProfile = () => {
      saveClientProfileDraft(client);
    };

    const hasChanges = hasClientProfileUnsavedChanges(client);

    const isDisabled = client.isOffline || !hasChanges;

    return (
      <HoverableTouchableOpacity
        onPress={saveClientProfile}
        disabled={isDisabled}
        style={{
          height: 40,
          minWidth: 100,
          borderRadius: 14,
          backgroundColor: Colors.bubbleColor,
          paddingHorizontal: 16,
          alignItems: "center",
          justifyContent: "center",
          opacity: isDisabled ? 0.5 : 1,
        }}
        hoverStyle={{
          backgroundColor: Colors.darkBubbleColor,
        }}
      >
        <Text style={[Fonts.medium14, { color: Colors.whiteColor }]}>
          {t("save-string")}
        </Text>
      </HoverableTouchableOpacity>
    );
  });

  const DeleteButton = () => {
    return (
      <HoverableTouchableOpacity
        onPress={() => {
          if (!client.isOffline) {
            setShowingDeleteAlert(true);
          }
        }}
        disabled={client.isOffline}
        style={{
          height: 40,
          paddingHorizontal: 16,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: "transparent",
          opacity: client.isOffline ? 0.5 : 1,
        }}
        hoverStyle={{
          backgroundColor: "rgba(240, 68, 56, 0.1)", // Light red background
        }}
      >
        <Text style={[Fonts.medium14, { color: Colors.redColor }]}>
          {t("deleteClient-string")}
        </Text>
      </HoverableTouchableOpacity>
    );
  };

  const Separator = () => {
    return <View style={styles.separator} />;
  };

  const DeleteSampleClientButton = () => {
    const isSampleClient = client.email && client.email.includes("efortcoach.com");

    if (!isSampleClient) return null;

    return (
      <View style={{ marginBottom: 16, alignSelf: "flex-end" }}>
        <HoverableTouchableOpacity
          onPress={() => {
            if (!client.isOffline) {
              deleteClient();
            }
          }}
          disabled={client.isOffline}
          style={{
            backgroundColor: Colors.redColor,
            borderRadius: 12,
            paddingVertical: 10,
            paddingHorizontal: 16,
            flexDirection: "row",
            alignItems: "center",
            opacity: client.isOffline ? 0.5 : 1,
          }}
          hoverStyle={{
            opacity: 0.85,
          }}
        >
          <Text style={[Fonts.medium14, { color: Colors.whiteColor, fontSize: 13 }]}>
            {t("deleteSampleAthlete-string")}
          </Text>
        </HoverableTouchableOpacity>
      </View>
    );
  };

  return (
    <View
      style={[
        styles.container,
        { paddingTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      {client && (
        <>
          <DeleteSampleClientButton />
          <View
            key={`client-profile-reset-${profileDiscardResetVersion}`}
            style={styles.card}
          >
            <View style={styles.row}>
              <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                {t("clientName-string")}
              </Text>
              <View style={[styles.rowContent, styles.nameRowContent]}>
                <TextName
                  property={"nameForCoach"}
                  firestoreProperty={"name_for_coach"}
                  placeholder={"name-string"}
                />
                <View style={styles.nameSurnameSpacer} />
                <TextName
                  property={"surnameForCoach"}
                  firestoreProperty={"surname_for_coach"}
                  placeholder={"surname-string"}
                />
              </View>
            </View>
            <Separator />
            <View style={styles.row}>
              <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                {t("email-string")}
              </Text>
              <View style={[styles.rowContent, styles.emailRowContent]}>
                <View style={styles.emailContentWrapper}>
                  <UnmutableTextName
                    property={"email"}
                    placeholder={"entrenador@gmail.com"}
                    textAlign="center"
                  />
                </View>
              </View>
            </View>
            <Separator />
            <View style={styles.row}>
              <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                {t("contactNumber-string")}
              </Text>
              <View style={styles.rowContent}>
                <TextName
                  property={"phone"}
                  firestoreProperty={"phone"}
                  placeholder={"phone-string"}
                />
              </View>
            </View>
            <Separator />
            <View style={styles.row}>
              <Text style={[Fonts.regular14, styles.rowLabel, { width: screenSize === "small" ? 120 : 180 }]}>
                {t("gender-string")}
              </Text>
              <View style={styles.rowContent}>
                <ProfileSegmentedControl
                  options={["female-string", "male-string"]}
                  value={client.clientSettings.gender}
                  onChange={(bool) => client.setSettings("gender", bool)}
                  width={screenSize === "small" ? "100%" : 260}
                />
              </View>
            </View>
            <Separator />
            
            <BestCompetitionLifts />

            <Separator />
            <View style={{ width: "100%" }}>
              <TextNotes />
            </View>
          </View>

          <View style={styles.actionsContainer}>
            <SaveButton />
            <DeleteButton />
          </View>

          {showingDeleteAlert && (
            <CustomAlert
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              action={deleteClient}
              specialCTA={"delete-string"}
            />
          )}
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: Colors.whiteColor,
    padding: 24,
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowLabel: {
    color: Colors.blackColor,
  },
  rowContent: {
    flexDirection: "row",
    flex: 1,
    alignItems: "center",
    justifyContent: "space-around",
    flexWrap: "wrap",
  },
  nameRowContent: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  emailRowContent: {
    justifyContent: "center",
  },
  emailContentWrapper: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  nameSurnameSpacer: {
    marginVertical: 6,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.borderColor,
    width: "100%",
    marginVertical: 20,
  },
  textInputStyle: {
    height: 40,
  },
  textInputContainer: {
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 40,
    justifyContent: "center",
  },
  competitionTextBoxContainer: {
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  competitionTextBoxInput: {
    width: "100%",
    textAlign: "center",
    color: Colors.blackColor,
    paddingVertical: 8,
    outlineStyle: "none",
  },
  bestLiftsContainer: {
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  liftRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  liftLabel: {
    color: Colors.blackColor,
  },
  liftTextBoxWrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
  },
  actionsContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 24,
    gap: 16,
  },
});

export default ClientProfile;
